import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from '~/components/Layout'
import { useNavigate, useParams } from 'react-router/dist'
import { FormProvider, useForm } from 'react-hook-form'
import axios, { CancelTokenSource } from 'axios'
import { El } from '~/components/Layout/style'
import notify from '~/components/Notify'
import WelcomePart from './WelcomePart'
import { RouteTypes } from '~/routes/constants'
import { Progress } from 'semantic-ui-react'
import FormPart from './FormPart'
import { ClimaRegistrationData, createRegistrationFormData } from '../utils'
import { ClimaProgramQuery } from '~/QueryHooks/ClimaProgramQuery'
import { ClimaProgramService } from '~/api/services/climaProgram'

const AddRegistration: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()

  const [termsAccepted, setTermsAccepted] = useState(false)
  const [termsSubmited, setTermsSubmited] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const cancelTokenRef = useRef<CancelTokenSource | null>(null)

  const methods = useForm({
    mode: 'onSubmit'
  })

  const {
    data: registrationData,
    isLoading: isLoadingRegData,
    isFetching: isFetchinRegData
  } = ClimaProgramQuery.getClimaRegOptionsData()

  const renderAcceptTermsAction = () => (
    <El.PageHeaderActionButton
      positive
      size='large'
      onClick={() => setTermsSubmited(true)}
      disabled={!termsAccepted}
    >
      {t('Accept')}
    </El.PageHeaderActionButton>
  )

  function renderWelcomePart() {
    const farmer = registrationData?.farmers?.find(farmer => farmer.id === id)
    return <WelcomePart
      farmer={farmer}
      handleClickTerms={(termsAccepted) => setTermsAccepted(termsAccepted)}
    />
  }

  async function onSubmit(data: ClimaRegistrationData) {
    data.farmerId = id
    const formData = createRegistrationFormData(data)
    const source = axios.CancelToken.source()
    cancelTokenRef.current = source
    setIsUploading(true)
    setUploadProgress(0)

    try {
      await ClimaProgramService.registerClimaProgram({
        formData,
        onUploadProgress: (progressEvent) => {
          const { loaded, total } = progressEvent
          if (total) {
            setUploadProgress(Math.round((loaded / total) * 100))
          }
        },
        cancelToken: source
      })
      notify(t('Registration successful'), 'success')
      navigate(RouteTypes.MANAGER_PROFILE_REGISTRATIONS)
    } catch (err) {
      if (axios.isCancel(err)) {
        notify(t('Upload canceled'), 'error')
      } else {
        notify(err?.response?.data?.message || t('General error'), 'error')
      }
    }
    setIsUploading(false)
    setUploadProgress(0)
  }

  function renderFormPart() {
    return <FormProvider {...methods}>
      <FormPart
        milkOptions={registrationData?.milkAmounts || []}
        concentratedFeedsAmounts={registrationData?.concentratedFeedsAmounts || []}
      />
    </FormProvider>
  }

  function renderRegisterAction() {
    return <El.PageHeaderActionButton>
      {t('Register')}
    </El.PageHeaderActionButton>
  }

  function renderRegistration() {
    if (isLoadingRegData) {
      return
    }

    return <>
      {isUploading && (
      <Progress
        percent={uploadProgress}
        indicating
        progress
        style={{ marginBottom: '1rem' }}
        />
      )}
      {termsAccepted && termsSubmited ? renderFormPart() : renderWelcomePart()}
    </>
  }

  return <Layout
    title={t('Registration')}
    segmented
    close={RouteTypes.MANAGER_PROFILE_REGISTRATIONS}
    isBusy={isLoadingRegData || isFetchinRegData}
    actions={!termsSubmited ? renderAcceptTermsAction() : renderRegisterAction()}
    onSubmit={termsSubmited && methods.handleSubmit(onSubmit)}
  >
    {renderRegistration()}
  </Layout>
}

export default AddRegistration
